<template>
  <div class="page-business-intelligence-emitters">
    <PageMainAction :label="$t('page.business-intelligence.emitter.add-dialog.title')" @click="openAddDialog" />
    <div class="__header">
      <div class="__header-actions">
        <BaseSearchBar
          class="__search-bar"
          :class="{ 'in-search-mode': isSearching }"
          v-model="search"
          name="search"
          type="text"
          :placeholder="$t('page.business-intelligence.emitter.search')"
        ></BaseSearchBar>
        <!-- Phones: the header's "+" (PageMainAction) instead. -->
        <BaseButton v-if="!isPhone" hasIconLeft @click.native="openAddDialog">
          <BaseIcon name="plus" />
          {{ $t('actions.add') }}
        </BaseButton>
      </div>
      <div class="__results">
        <span class="__count">
          <span class="number-monospace">{{ formatLocalizedNumber(emittersTotalCount) }}</span>
          {{ ' ' + $tc('page.business-intelligence.results', emittersTotalCount) }}
        </span>
        <BaseRefreshButton :pending="emittersPending" @refresh="onRefresh" />
      </div>
    </div>
    <div class="__content">
      <!-- Phones: a single tab is only a title, which the section and the count already give. -->
      <v-tabs v-if="!isPhone" v-model="activeTab">
        <v-tab v-for="tab in tabs" :key="tab.key">
          <span>{{ tab.name }}</span>
        </v-tab>
      </v-tabs>

      <TableState
        v-if="!hasResults"
        :state-loading="emittersPending"
        :state-no-data="!emittersPending"
        :state-text="
          emittersPending ? $t('page.business-intelligence.loading') : $t('page.business-intelligence.no-result')
        "
      />
      <v-overlay :value="hasResults && emittersPending" absolute opacity="0.5" color="#ffffff" z-index="1"></v-overlay>
      <TableNormal
        item-key="id"
        v-if="hasResults"
        :fixed-header="true"
        :headers="headers"
        :items="emitters"
        :items-per-page="itemsPerPage"
        hide-default-footer
        class="bi-emitters-table"
        :has-more="isPhone && emittersPage < emittersTotalPages"
        :loading-more="emittersLoadingMore"
        :load-more-error="emittersLoadMoreError"
        :pending="emittersPending"
        @click:row="goToEmitter"
        @load-more="loadMore"
      >
        <template v-slot:item.name="{ item }">
          <div class="__row-name">
            <router-link class="__row-link" :to="emitterRoute(item)" @click.native.stop>
              {{ item.name }}
            </router-link>
            <BaseTag tone="neutral" preset="soft" icon="at-sign">
              {{ aliasCount(item) }}
            </BaseTag>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="__actions">
            <BaseMoreActions
              v-if="isPhone"
              :title="item.name"
              :actions="rowActions"
              @select="onRowAction($event, item)"
            />
            <v-menu v-else>
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="__more-button"
                  @click.stop
                  v-on="on"
                  v-bind="attrs"
                  :aria-label="$t('actions.more-actions')"
                >
                  <BaseIcon name="more-horizontal" class="__more" />
                </button>
              </template>
              <div class="__actions-menu base-actions-menu">
                <ul role="menu">
                  <li
                    role="menuitem"
                    tabindex="0"
                    @click="onExternalLinkClicked(item)"
                    @keydown.enter.space.prevent="onExternalLinkClicked(item)"
                  >
                    <BaseIcon name="external-link" />
                    <span>{{ $t('page.business-intelligence.emitter.open-in-new-tab') }}</span>
                  </li>
                  <li
                    role="menuitem"
                    tabindex="0"
                    @click="openEditDialog(item)"
                    @keydown.enter.space.prevent="openEditDialog(item)"
                  >
                    <BaseIcon name="edit-2" />
                    <span>{{ $t('actions.edit') }}</span>
                  </li>
                  <li
                    class="delete"
                    role="menuitem"
                    tabindex="0"
                    @click="openDeleteDialog(item)"
                    @keydown.enter.space.prevent="openDeleteDialog(item)"
                  >
                    <BaseIcon name="trash-2" />
                    <span>{{ $t('actions.delete') }}</span>
                  </li>
                </ul>
              </div>
            </v-menu>
          </div>
        </template>
      </TableNormal>
      <!-- Phones load more at the bottom of the list instead. -->
      <div class="bi-emitters-table__pagination" v-if="emittersTotalPages > 1 && !isPhone">
        <TablePagination
          v-model="currentPage"
          :disabled="emittersPending"
          :length="emittersTotalPages"
          :total-visible="isMobile ? 6 : 10"
          @input="onChangePage"
        />
      </div>
    </div>
    <EmitterFormDialog ref="emitterFormDialog" @action-succeeded="onRefresh" />
    <DeleteEmitterDialog ref="deleteEmitterDialog" @action-succeeded="onDeleted" />
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { formatLocalizedNumber } from '@/util/number-util';
import { viewport } from '@/util/viewport';

import TableNormal from '@/components/table/TableNormal.vue';
import TableState from '@/components/table/TableState.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseSearchBar from '@/components/base/BaseSearchBar.vue';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import DebounceMixin from '@/mixins/debounce-mixin';
import IsMobileMixin from '@/mixins/is-mobile-mixin';
import EmitterFormDialog from './EmitterFormDialog.vue';
import DeleteEmitterDialog from './DeleteEmitterDialog.vue';

export default {
  name: 'PageBIEmitters',
  mixins: [DebounceMixin, IsMobileMixin],
  components: {
    BaseMoreActions,
    BaseRefreshButton,
    PageMainAction,
    TableNormal,
    TableState,
    TablePagination,
    BaseButton,
    BaseSearchBar,
    BaseIcon,
    BaseTag,
    EmitterFormDialog,
    DeleteEmitterDialog,
  },
  computed: {
    ...mapGetters('biDashboard', [
      'emitters',
      'emittersPending',
      'emittersPage',
      'emittersTotalCount',
      'emittersTotalPages',
      'emittersLoadingMore',
      'emittersLoadMoreError',
    ]),
    isPhone() {
      return viewport.size === 'phone';
    },
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions() {
      return [
        {
          key: 'open',
          label: this.$t('page.business-intelligence.emitter.open-in-new-tab'),
          icon: 'external-link',
        },
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    headers() {
      return [
        {
          text: this.$t('page.business-intelligence.emitter.data-table-titles.name'),
          value: 'name',
          sortable: false,
        },
        {
          text: this.$t('page.business-intelligence.emitter.data-table-titles.actions'),
          sortable: false,
          align: 'left',
          value: 'actions',
          width: '100px',
        },
      ];
    },
    hasResults() {
      return this.emitters.length > 0;
    },
    isSearching() {
      return this.search.trim() !== '';
    },
  },
  watch: {
    // the search goes to the API: wait for the typing to pause, then restart from the first page
    search() {
      this.debounce(() => this.fetchEmitters(1), 1000);
    },
    // A phone lists from the first page, and the larger screens show one page at a time.
    isPhone() {
      this.fetchEmitters(1);
    },
  },
  methods: {
    ...mapActions('biDashboard', ['getEmitters', 'loadMoreEmitters', 'reloadListedEmitters']),
    formatLocalizedNumber,
    // The list row carries the emitter name and how many aliases it has. The count
    // field is read by name, or from the alias list when the API sends it instead.
    aliasCount(item) {
      if (typeof item.normalized_names_count === 'number') return item.normalized_names_count;
      const names = item.emitter_names || item.normalized_names;
      return Array.isArray(names) ? names.length : 0;
    },
    async fetchEmitters(page = 1) {
      this.currentPage = page;
      await this.getEmitters({ page, search: this.search });
      // A refresh (rename, delete, search) can shrink the results below the page
      // we were on; fall back to the last page instead of an empty one.
      const lastPage = Math.max(1, this.emittersTotalPages || 1);
      if (this.currentPage === page && !this.emittersPending && page > lastPage) {
        await this.fetchEmitters(lastPage);
      }
    },
    onChangePage(page = 1) {
      this.fetchEmitters(page);
    },
    // Phones list the emitters page after page.
    loadMore() {
      // Asking for a page now would cancel the search that's running.
      if (this.emittersPending || this.emittersPage >= this.emittersTotalPages) return;
      this.loadMoreEmitters({ search: this.search });
    },
    onRowAction(key, emitter) {
      if (key === 'open') this.onExternalLinkClicked(emitter);
      else if (key === 'edit') this.openEditDialog(emitter);
      else this.openDeleteDialog(emitter);
    },
    emitterRoute(emitter) {
      return {
        name: 'business-intelligence-emitter-performance',
        params: { emitterId: emitter.id },
        query: { name: emitter.name },
      };
    },
    goToEmitter(emitter) {
      this.$router.push(this.emitterRoute(emitter));
    },
    openAddDialog() {
      this.$refs.emitterFormDialog.open();
    },
    openEditDialog(emitter) {
      this.$refs.emitterFormDialog.open(emitter);
    },
    openDeleteDialog(emitter) {
      this.$refs.deleteEmitterDialog.open(emitter);
    },
    // The page on screen again; on a phone, every page listed, so the reader keeps their place.
    onRefresh() {
      if (this.isPhone && this.emittersPage > 1) this.reloadListedEmitters({ search: this.search });
      else this.fetchEmitters(this.currentPage);
    },
    onDeleted() {
      if (this.isPhone) {
        this.onRefresh();
        return;
      }
      // the row is already gone from the store; go back a page when it was the last one
      const page = this.emitters.length === 0 && this.currentPage > 1 ? this.currentPage - 1 : this.currentPage;
      this.fetchEmitters(page);
    },
    onExternalLinkClicked(emitter) {
      const routeData = this.$router.resolve(this.emitterRoute(emitter));
      window.open(routeData.href, '_blank', 'noopener,noreferrer');
    },
  },
  data() {
    return {
      itemsPerPage: 25,
      currentPage: 1,
      activatedOnce: false,
      activeTab: 0,
      search: '',
      tabs: [{ key: 'all', name: this.$t('page.business-intelligence.emitter.tabs.all') }],
    };
  },
  async created() {
    await this.fetchEmitters(1);
  },
  // Cached by keep-alive: coming back (e.g. after a rename on the detail page)
  // reloads the current page so the server-side search, sort and paging apply.
  activated() {
    if (this.activatedOnce) this.onRefresh();
    this.activatedOnce = true;
  },
};
</script>

<style lang="scss">
.page-business-intelligence-emitters {
  padding: 32px;

  @include screen-below('tablet') {
    padding: $phone-page-gutter;
  }

  .__actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  > .__header {
    padding-bottom: 16px;

    .__header-actions {
      display: flex;
      align-items: flex-end;
      padding-bottom: 16px;
      justify-content: space-between;
      gap: 8px;

      .__search-bar {
        &.in-search-mode {
          .base-icon {
            color: $color-accent;
          }
          .base-search-bar__input {
            border: 1px solid $color-accent;
          }
        }
      }
    }

    .__results {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .__count {
      color: $color-neutral;
      font-size: $font-size-small;
      padding: 4px 12px;
      text-transform: uppercase;
    }
  }

  > .__content {
    border-radius: $border-radius-root;
    position: relative;

    > .v-tabs {
      border-top-left-radius: $border-radius-root;
      border-top-right-radius: $border-radius-root;

      .v-tab {
        text-transform: none;
        color: $color-normal-pale;

        &.v-tab--active {
          color: $color-normal;
          font-weight: 600;
        }
      }
    }
    .bi-emitters-table {
      tr th {
        font-size: $font-size-small !important;
      }
      tbody {
        td {
          color: $color-normal;
          cursor: pointer;

          &:first-child {
            padding-left: 16px !important;
          }

          .__more {
            cursor: pointer;
            margin-right: 24px;
          }
        }
      }

      .__row-name {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;

        .__row-link {
          color: inherit;
          text-decoration: none;

          &:focus-visible {
            outline: 2px solid $color-accent;
            outline-offset: 2px;
            border-radius: 2px;
          }
        }
      }

      .__more-button {
        display: flex;
        align-items: center;
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;

        &:focus-visible {
          outline: 2px solid $color-accent;
          outline-offset: 2px;
          border-radius: 2px;
        }
      }

      &__pagination {
        align-items: center;
        display: flex;
        justify-content: center;
        margin: 0 auto;
        padding: 32px 32px 0;
        width: 100%;
      }
    }
  }
}

// action menus are portaled by v-menu, so the focus style must be top-level
.base-actions-menu li:focus-visible {
  background: $color-hover;
  outline: none;
}
</style>
